import { LogOut } from 'lucide-react';
import { getAccountRoles, getRoleCapabilitySummary, type Role } from '@rb/shared/admin';
import { findVerse } from '@rb/shared/admin-verses';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useAdminAuth } from '@/auth/use-admin-auth';
import { useActiveVerseStore } from '@/verse/active-verse.store';

/**
 * Sidebar footer — 현재 account 의 role 과 한 줄 capability 요약, 그리고 logout.
 * UI 게이트는 하지 않는다 (모든 액션 노출, 권한 부족은 서버가 throw → 브라우저
 * alert). 운영자가 자신이 무엇을 할 수 있는지 한눈에 확인하는 용도.
 */
export function RoleBadge() {
  const { account, logout } = useAdminAuth();
  const activeVerseId = useActiveVerseStore((s) => s.activeVerseId);

  if (!account) return null;

  const verseEntry = activeVerseId ? findVerse(activeVerseId) : undefined;
  const env = verseEntry?.alias ?? activeVerseId ?? 'unknown';
  const roles = getAccountRoles(account);
  const summary = getRoleCapabilitySummary(account, env);
  const primary: Role | undefined = roles[0];
  const shortAcct = `${account.slice(0, 6)}…${account.slice(-4)}`;

  return (
    <div className="flex flex-col gap-2 rounded-md border border-sidebar-border bg-sidebar-accent/40 p-3 text-xs">
      <div className="flex items-center justify-between gap-2">
        <span className="break-all font-mono" title={account}>
          {shortAcct}
        </span>
        {primary ? (
          <Badge variant="secondary" className="uppercase">
            {primary}
          </Badge>
        ) : (
          <Badge variant="destructive">no role</Badge>
        )}
      </div>
      <div className="text-muted-foreground">{env}</div>
      <Separator />
      <div className="text-muted-foreground">{summary || 'No admin permissions.'}</div>
      <Button variant="ghost" size="sm" onClick={() => logout()} className="justify-start gap-2">
        <LogOut className="size-3.5" />
        Sign out
      </Button>
    </div>
  );
}
